import { matchesHaystack, normalizeSearch } from '@/lib/search-haystack';

import type { SettingsSearchEntry } from '@/lib/settings-search-index';

/**
 * Which index entries a query matches, and in what order.
 *
 * The matching rule is the catalog's, reused rather than reinvented
 * (`search-haystack.ts`): normalize away case and diacritics, then require
 * EVERY whitespace-separated term to appear somewhere in the entry's haystack.
 * Two properties matter and neither is decoration:
 *
 *  - **Diacritics fold.** An operator working in French types "réponse" for a
 *    card whose keyword bag says "reponse", and a strict comparison silently
 *    finds nothing — in exactly the locales where finding things is hardest.
 *  - **Terms are ANDed, and matching is substring.** "delete workspace" finds
 *    the delete card without also returning every setting containing either
 *    word, and "conv" finds "Conversation retention" while somebody is still
 *    typing.
 *
 * No `\b` anywhere. JavaScript's word boundary is ASCII-based even under the
 * `u` flag, so a boundary-anchored pattern never fires before Arabic, Hebrew, or
 * a Latin word starting with an accented letter — dead in some locales, green in
 * every test written in English (`.claude/rules/quality-bar.md`).
 */

/** How well a query matched, best first. Lower is better. */
const enum MatchRank {
  /** The label starts with the query — almost certainly the thing meant. */
  LabelPrefix = 0,
  /** The label contains it somewhere. */
  LabelContains = 1,
  /** Only the breadcrumb or the keyword bag matched. */
  Elsewhere = 2,
}

/**
 * Settings lead, then their controls, then pages.
 *
 * The operator decision behind the surface: "which option does this?" is the
 * question it exists for, and "which page is that?" is the fallback. A page that
 * matched by prefix still sits below a setting that matched by keyword, which is
 * deliberate — the alternative buries the options under the nav.
 */
const KIND_RANK: Readonly<Record<SettingsSearchEntry['kind'], number>> = {
  setting: 0,
  control: 1,
  page: 2,
};

/** How many rows a dropdown can show before it stops being scannable. */
export const SETTINGS_SEARCH_LIMIT = 10;

function matchRank(entry: SettingsSearchEntry, normalizedQuery: string): MatchRank {
  const label = normalizeSearch(entry.label);
  if (label.startsWith(normalizedQuery)) return MatchRank.LabelPrefix;
  if (label.includes(normalizedQuery)) return MatchRank.LabelContains;
  return MatchRank.Elsewhere;
}

/**
 * The rows to show for `query`, ranked and capped.
 *
 * An empty or whitespace-only query returns NOTHING, which is the opposite of
 * `matchesHaystack`'s own "matches everything" and is right here: the top-bar
 * dropdown opens on what you typed, and opening it on the entire index the
 * moment the field is focused is a wall, not an answer. The ⌘K palette keeps its
 * own show-everything behaviour, because there the empty state is the launcher.
 */
export function rankSettingsSearch(
  entries: readonly SettingsSearchEntry[],
  query: string,
  limit: number = SETTINGS_SEARCH_LIMIT,
): readonly SettingsSearchEntry[] {
  const normalizedQuery = normalizeSearch(query).trim();
  if (normalizedQuery.length === 0) return [];

  return entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => matchesHaystack(entry.haystack, query))
    .sort((a, b) => {
      const kind = KIND_RANK[a.entry.kind] - KIND_RANK[b.entry.kind];
      if (kind !== 0) return kind;
      const rank = matchRank(a.entry, normalizedQuery) - matchRank(b.entry, normalizedQuery);
      if (rank !== 0) return rank;
      // Registry order last, so equal matches keep the order the operator would
      // meet them in on the page rather than an arbitrary one.
      return a.index - b.index;
    })
    .slice(0, limit)
    .map(({ entry }) => entry);
}
